// FormRoles.jsx (phosphor-react + revised components + cards layout for permissions)
import React, { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import axiosInstance from "../../utils/axios/axiosConfig"
import { toast } from "sonner"
import { motion } from "framer-motion"
import { Loading, TextInput, TextArea, Checkbox } from "../../components"
import { FloppyDiskBack, ShieldCheck, Rows, CheckSquare, Square } from "phosphor-react"

function FormRoles() {
  const [sectionsPermissions, setSectionsPermissions] = useState([])
  const [roleInfo, setRoleInfo] = useState({ name: "", description: "" })
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const navigate = useNavigate()
  const { id } = useParams()

  useEffect(() => {
    if (id) getRoleToUpdate()
    else initializeSection()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const initializeSection = () => {
    setRoleInfo({ name: "", description: "" })
    setSectionsPermissions([
      { section: "users", access: { create: false, read: false, update: false, delete: false } },
      { section: "roles", access: { create: false, read: false, update: false, delete: false } },
      { section: "categories", access: { create: false, read: false, update: false, delete: false } },
      { section: "brands", access: { create: false, read: false, update: false, delete: false } },
      { section: "contacts", access: { create: false, read: false, update: false, delete: false } },
      { section: "products", access: { create: false, read: false, update: false, delete: false } },
      { section: "blogs", access: { create: false, read: false, update: false, delete: false } },
      { section: "orders", access: { create: false, read: false, update: false, delete: false } },
      { section: "reviews", access: { create: false, read: false, update: false, delete: false } },
      { section: "pixels", access: { create: false, read: false, update: false, delete: false } },
      { section: "settings", access: { create: false, read: false, update: false, delete: false } },
    ])
  }

  const initializeUpdateSection = (role) => {
    setRoleInfo({ name: role?.name || "", description: role?.description || "" })
    setSectionsPermissions(Array.isArray(role?.permissions) ? role.permissions : [])
  }

  const isAllChecked = (access) => Object.values(access || {}).every(Boolean)

  const handleCheckboxChange = (section, permission) => (e) => {
    setSectionsPermissions((prev) =>
      prev.map((perm) =>
        perm.section === section
          ? { ...perm, access: { ...perm.access, [permission]: e.target.checked } }
          : perm
      )
    )
  }

  const handleSectionCheckAll = (section) => (e) => {
    const isChecked = e.target.checked
    setSectionsPermissions((prev) =>
      prev.map((perm) =>
        perm.section === section
          ? { ...perm, access: { create: isChecked, read: isChecked, update: isChecked, delete: isChecked } }
          : perm
      )
    )
  }

  const getRoleToUpdate = async () => {
    try {
      setErrorMessage("")
      setLoading(true)
      const response = await axiosInstance.get(`/role/get/${id}`)
      initializeUpdateSection(response?.data?.data)
    } catch (error) {
      const msg = error?.response?.data?.message || "Role fetch failed"
      setErrorMessage(msg)
      toast.error(msg)
      console.error("Role fetch failed:", error?.response?.data || error)
    } finally {
      setLoading(false)
    }
  }

  const save = async (e) => {
    e.preventDefault()
    try {
      setErrorMessage("")
      setLoading(true)

      const payload = {
        name: roleInfo.name,
        description: roleInfo.description,
        permissions: sectionsPermissions,
      }

      const response = id
        ? await axiosInstance.put(`/role/update/${id}`, payload)
        : await axiosInstance.post("/role/create", payload)

      toast.success(response?.data?.message || "Saved")
      if (id) getRoleToUpdate()
      else navigate(-1)
    } catch (error) {
      const msg = error?.response?.data?.message || "Role save failed"
      setErrorMessage(msg)
      toast.error(msg)
      console.error("Role save failed:", error?.response?.data || error)
    } finally {
      setLoading(false)
    }
  }

  const pageTitle = id ? "Update Role" : "New Role"

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full h-full px-6 py-5 bg-neutral-50/80 dark:bg-neutral-950/40"
    >
      {loading && <Loading />}

      <div className="flex flex-col space-y-5">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="text-start">
            <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">{pageTitle}</h1>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              {id ? "Update an existing role" : "Create a new role"} by entering the name, description, and permissions.
            </p>
            {errorMessage ? (
              <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
                {errorMessage}
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/Roles"
              className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800"
            >
              Cancel
            </Link>
            <button
              type="button"
              onClick={save}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-xl hover:bg-cyan-400 focus:outline-none"
            >
              <FloppyDiskBack size={18} />
              {id ? "Update" : "Save"} Role
            </button>
          </div>
        </div>

        {/* Role info */}
        <div className="grid gap-4 md:grid-cols-2">
          <TextInput
            label="Name"
            value={roleInfo.name}
            onChange={(e) => setRoleInfo((s) => ({ ...s, name: e.target.value }))}
            placeholder="Enter role name"
          />
          <TextInput
            label="Description"
            value={roleInfo.description}
            onChange={(e) => setRoleInfo((s) => ({ ...s, description: e.target.value }))}
            placeholder="Enter role description"
          />
        </div>

        {/* Permissions (cards) */}
        <div className="rounded-2xl border border-neutral-200/70 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-200/70 dark:border-neutral-800 flex items-center justify-between">
            <div className="text-start">
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} className="text-neutral-700 dark:text-neutral-200" />
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Permissions</h3>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                Toggle read/create/update/delete per section.
              </p>
            </div>
          </div>

          <div className="p-4">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {sectionsPermissions.map((item) => {
                const all = isAllChecked(item.access)
                return (
                  <div
                    key={item.section}
                    className="rounded-2xl border border-neutral-200/70 dark:border-neutral-800 bg-white/70 dark:bg-neutral-950/40 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-start">
                        <div className="text-sm font-semibold text-neutral-900 dark:text-white capitalize">
                          {item.section}
                        </div>
                        <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                          Enable specific actions for this section.
                        </div>
                      </div>

                      <label className="inline-flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={all}
                          onChange={handleSectionCheckAll(item.section)}
                          className="peer absolute h-0 w-0 opacity-0"
                        />
                        <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-900/70 px-2.5 py-1">
                          {all ? <CheckSquare size={16} /> : <Square size={16} />}
                          All
                        </span>
                      </label>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <Checkbox
                        checked={!!item.access.read}
                        onChange={handleCheckboxChange(item.section, "read")}
                        label="Read"
                      />
                      <Checkbox
                        checked={!!item.access.create}
                        onChange={handleCheckboxChange(item.section, "create")}
                        label="Create"
                      />
                      <Checkbox
                        checked={!!item.access.update}
                        onChange={handleCheckboxChange(item.section, "update")}
                        label="Update"
                      />
                      <Checkbox
                        checked={!!item.access.delete}
                        onChange={handleCheckboxChange(item.section, "delete")}
                        label="Delete"
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default FormRoles
